import { AlertTriangle, CalendarDays, CheckCircle2, LayoutDashboard, Search, Users, XCircle } from "lucide-react";
import { cssVars } from "@/lib/theme/colors";

const ROWS = [
  { name: "Example client A", type: "Initial consult", status: "paid" },
  { name: "Example client B", type: "Clinical Pilates", status: "unpaid" },
  { name: "Example client C", type: "Review", status: "cancelled" },
];

function Status({ s }) {
  if (s === "paid") return <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-2xs font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" aria-hidden />Paid</span>;
  if (s === "unpaid") return <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-2xs font-medium text-amber-900"><AlertTriangle className="h-3 w-3" aria-hidden />Unpaid</span>;
  return <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-2xs font-medium text-red-800"><XCircle className="h-3 w-3" aria-hidden />Cancelled</span>;
}

function Nav({ icon: Icon, label, active }) {
  return (
    <div className={active ? "relative flex items-center gap-1.5 rounded-md bg-le-selected px-2 py-1.5 text-xs font-medium text-le-primary-deep" : "flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-le-t-2"}>
      {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-le-primary" aria-hidden />}
      <Icon className={active ? "h-3.5 w-3.5 text-le-primary-text" : "h-3.5 w-3.5"} aria-hidden />{label}
    </div>
  );
}

// Mini, non-interactive mock of the app shell rendered with the given (hex) theme vars.
export default function ThemePreview({ vars }) {
  return (
    <div style={cssVars(vars)} className="overflow-hidden rounded-xl border border-le-border bg-le-page text-le-text shadow-sm" data-testid="theme-preview" aria-label="Theme preview" role="img">
      <div className="flex h-10 items-center gap-2 border-b border-le-border bg-le-surface px-3">
        <div className="h-5 w-5 rounded bg-le-primary" />
        <span className="font-display text-sm font-bold">Living Energy Hub</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-md border border-le-border px-2 py-0.5 text-2xs text-le-t-muted"><Search className="h-3 w-3" aria-hidden />Search…</span>
      </div>
      <div className="flex">
        <div className="w-32 shrink-0 space-y-0.5 border-r border-le-border bg-le-sidebar p-2">
          <div className="px-2 pb-1 text-2xs font-semibold text-le-t-2">Today&apos;s work</div>
          <Nav icon={LayoutDashboard} label="Dashboard" />
          <Nav icon={CalendarDays} label="Appointments" active />
          <Nav icon={Users} label="Clients" />
        </div>
        <div className="min-w-0 flex-1 space-y-3 p-3">
          <div className="font-display text-base font-bold">Appointments</div>
          <div className="space-y-2 rounded-lg border border-le-border bg-le-surface p-2.5">
            <div className="text-2xs font-medium text-le-t-2">Client name or email</div>
            <div className="rounded-md border border-le-input-border bg-le-surface px-2 py-1 text-xs text-le-t-muted">e.g. jane@…</div>
            <div className="flex gap-1.5">
              <span className="rounded-md bg-le-primary px-2.5 py-1 text-xs font-medium text-white">Apply</span>
              <span className="rounded-md border border-le-input-border bg-le-surface px-2.5 py-1 text-xs font-medium">Download CSV</span>
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border border-le-border bg-le-surface">
            <div className="grid grid-cols-[1.3fr_1fr_auto] gap-2 border-b border-le-border bg-le-subtle px-2.5 py-1.5 text-2xs font-semibold text-le-t-2">
              <span>Client</span><span>Type</span><span>Paid</span>
            </div>
            {ROWS.map((r, i) => (
              <div key={r.name} className={`grid grid-cols-[1.3fr_1fr_auto] items-center gap-2 px-2.5 py-1.5 text-xs ${i < 2 ? "border-b border-le-border-subtle" : ""} ${i === 1 ? "bg-le-sidebar/50" : ""}`}>
                <span className="truncate font-medium">{r.name}</span>
                <span className="truncate text-le-t-2">{r.type}</span>
                <Status s={r.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
